import React from "react";

/**
 * Floating Telegram button — small, professional, pulsing glow.
 * Fixed at bottom-right corner of viewport, opens official channel in new tab.
 * Support chat is handled separately via the hamburger menu's "Support" item.
 */
const TELEGRAM_CHANNEL_URL = "https://t.me/+m45ZCZcZJoswNzI1";

export function TelegramFAB() {
  return (
    <>
      <style>{`
        @keyframes tg-fab-glow {
          0%, 100% {
            box-shadow:
              0 4px 14px rgba(0, 136, 204, 0.40),
              0 0 0 0 rgba(36, 161, 222, 0.55),
              0 0 18px rgba(36, 161, 222, 0.25);
          }
          50% {
            box-shadow:
              0 4px 14px rgba(0, 136, 204, 0.55),
              0 0 0 10px rgba(36, 161, 222, 0.00),
              0 0 28px rgba(36, 161, 222, 0.55);
          }
        }
        @keyframes tg-fab-pulse-ring {
          0%   { transform: scale(1);   opacity: 0.55; }
          100% { transform: scale(1.85); opacity: 0;    }
        }
        @keyframes tg-fab-sheen {
          0%   { transform: translateX(-120%) skewX(-20deg); }
          60%  { transform: translateX(220%)  skewX(-20deg); }
          100% { transform: translateX(220%)  skewX(-20deg); }
        }
        .tg-fab          { animation: tg-fab-glow 2.4s ease-in-out infinite; }
        .tg-fab:hover    { transform: translateY(-2px) scale(1.08); animation-duration: 1.2s; }
        .tg-fab .tg-ring { animation: tg-fab-pulse-ring 2.4s ease-out infinite; }
        .tg-fab .tg-sheen{ animation: tg-fab-sheen 3.6s ease-in-out infinite; }
        @media (max-width: 640px) {
          /* On mobile, lift the FAB ABOVE the bottom tab bar so it never covers
             the Wallet/Dashboard/Teams nav buttons. The bottom nav is ~70px tall
             with safe-area inset; we leave a comfortable gap above it. */
          .tg-fab-wrap {
            right: 12px !important;
            bottom: calc(82px + env(safe-area-inset-bottom, 0px)) !important;
          }
        }
      `}</style>

      <div
        className="tg-fab-wrap"
        style={{
          position: "fixed",
          right: "20px",
          bottom: "20px",
          zIndex: 9999,
          pointerEvents: "none",
        }}
        data-testid="telegram-fab-wrap"
      >
        <a
          href={TELEGRAM_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Join our Telegram channel"
          title="Join us on Telegram"
          data-testid="telegram-fab-link"
          className="tg-fab"
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "46px",
            height: "46px",
            borderRadius: "50%",
            background:
              "radial-gradient(135% 135% at 30% 25%, #4FC3F7 0%, #29B6F6 35%, #1E88E5 70%, #0277BD 100%)",
            border: "1px solid rgba(255,255,255,0.20)",
            cursor: "pointer",
            pointerEvents: "auto",
            textDecoration: "none",
            transition: "transform 220ms ease",
            overflow: "hidden",
          }}
        >
          <span
            className="tg-ring"
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: "2px solid rgba(36, 161, 222, 0.55)",
              pointerEvents: "none",
            }}
          />
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "3px",
              left: "3px",
              right: "3px",
              height: "45%",
              borderRadius: "50% 50% 45% 45% / 60% 60% 40% 40%",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.40) 0%, rgba(255,255,255,0.05) 100%)",
              pointerEvents: "none",
            }}
          />
          <span
            className="tg-sheen"
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "40%",
              height: "100%",
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)",
              filter: "blur(2px)",
              pointerEvents: "none",
            }}
          />
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="#FFFFFF"
            aria-hidden="true"
            style={{
              position: "relative",
              zIndex: 2,
              filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.20))",
            }}
          >
            <path d="M21.426 2.574a1.5 1.5 0 0 0-1.59-.25L2.6 9.51a1.25 1.25 0 0 0 .063 2.33l4.16 1.41 1.59 5.06a1 1 0 0 0 1.66.42l2.49-2.4 4.36 3.2a1.25 1.25 0 0 0 1.97-.74l3.13-14.21a1.5 1.5 0 0 0-.6-1.6Zm-3.93 4.36-7.3 6.62-.27 3.34-1.21-3.83 8.78-6.13Z" />
          </svg>
        </a>
      </div>
    </>
  );
}

/**
 * Shared helper: opens the Telegram support 1-on-1 chat in a new tab.
 * The support username is intentionally NOT rendered anywhere on the page —
 * users only see a "Support" label, the actual handle is private.
 *
 * Use this from menu items like:
 *   <button onClick={openTelegramSupport}>Support</button>
 */
const SUPPORT_TG_USERNAME = "Smarthub_1";
export function openTelegramSupport() {
  const url = `https://t.me/${SUPPORT_TG_USERNAME}`;
  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    window.location.href = url;
  }
}

export default TelegramFAB;
